import React, { useMemo, useRef, useState } from "react"
import useTitle from "@/hooks/useTitle"
import { addIcon } from "@/assets/img/imgAssets"
import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline"
import {
  UserCircleIcon,
  BuildingOffice2Icon,
  ArrowUpRightIcon,
} from "@heroicons/react/24/solid"
import PulseLoader from "react-spinners/PulseLoader"

import { useGetAccountsQuery } from "../accounts/accountsApiSlice"
import { useGetContactsQuery } from "../contacts/contactsApiSlice"
import {
  useGetAccountContactMapsQuery,
  useCreateNewAccountContactMapMutation,
} from "./accountContactMapsApiSlice"

const EtAccountContactMapList = ({
  setActiveAdminView,
  onAdd,
  onUpdate,
  onDelete,
  setSelectedEntityId,
  userOrgId = "",
}) => {
  // First Created - 27 Apr 2026
  // Author - Atul Pandey
  // Purpose - To list and manage Account Contact Map entries
  // Updated - 28 Apr 2026 - Refined for account map visualization - tabbed views
  // Updated - 28 Apr 2026 - added chevron icons and useRef for scrolling account cards

  useTitle("Account Contact Map")

  // 1 - State Variables
  const [selectedAccountId, setSelectedAccountId] = useState("")
  const [showDetails, setShowDetails] = useState({})
  const [autoCreateMessage, setAutoCreateMessage] = useState("")
  const [activeTab, setActiveTab] = useState("details") // 28 Apr 2026

  // 2 - Data
  const {
    data: accountsData,
    isLoading: isAccountsLoading,
    isError: isAccountsError,
    error: accountsError,
  } = useGetAccountsQuery({ ownerOrgId: userOrgId })

  const {
    data: contactsData,
    isLoading: isContactsLoading,
    isError: isContactsError,
    error: contactsError,
  } = useGetContactsQuery()

  const {
    data: accountContactMapsData,
    isLoading: isMapsLoading,
    isError: isMapsError,
    error: mapsError,
  } = useGetAccountContactMapsQuery(
    { accountId: selectedAccountId },
    { skip: !selectedAccountId }
  )

  const [
    createNewAccountContactMap,
    { isLoading: isCreatingMap },
  ] = useCreateNewAccountContactMapMutation()

  const accounts =
    accountsData?.ids?.map(id => accountsData?.entities[id]) || []

  const contacts =
    contactsData?.ids?.map(id => contactsData?.entities[id]) || []

  const accountContactMaps =
    accountContactMapsData?.ids?.map(id => accountContactMapsData?.entities[id]) || []


// console.log('accountContactMaps :', accountContactMaps)

  const selectedAccount = accounts?.find(
    account => account?._id === selectedAccountId || account?.id === selectedAccountId
  )

  const selectedAccountContactOrgId =
    selectedAccount?.contactOrganizationId?._id ||
    selectedAccount?.contactOrganizationId?.id ||
    selectedAccount?.contactOrganizationId ||
    ""

  const eligibleContacts = contacts?.filter(contact => {
    const contactOrgId =
      contact?.contactOrgId?._id ||
      contact?.contactOrgId?.id ||
      contact?.contactOrgId ||
      ""

    return contactOrgId?.toString() === selectedAccountContactOrgId?.toString()
  })

  const existingMappedContactIds = accountContactMaps
    ?.map(mapEntry =>
      mapEntry?.contactId?._id ||
      mapEntry?.contactId?.id ||
      mapEntry?.contactId
    )
    ?.filter(Boolean)

  const missingContacts = eligibleContacts?.filter(contact => {
    const contactId = contact?._id || contact?.id
    return !existingMappedContactIds?.includes(contactId)
  })

  // 3a - Handle Toggle Details
  const handleToggleDetails = (mapEntryId) => {
    setShowDetails(prev => ({
      ...prev,
      [mapEntryId]: !prev[mapEntryId],
    }))
  }

  // 3b - Handle Auto Create Contact Map
  const handleAutoCreateContactMap = async () => {
    if (!selectedAccountId) {
      setAutoCreateMessage("Please select an Account first.")
      return
    }

    if (!missingContacts?.length) {
      setAutoCreateMessage("No missing contacts found for this Account.")
      return
    }

    try {
      for (const contact of missingContacts) {
        await createNewAccountContactMap({
          accountId: selectedAccountId,
          contactId: contact?._id || contact?.id,
          relationshipStrength: "Unknown",
          relationshipSentiment: "Unknown",
          accessLevel: "NoAccess",
        }).unwrap()
      }

      setAutoCreateMessage(
        `${missingContacts.length} contact(s) added to the Account Contact Map.`
      )
    } catch (err) {
      console.error(err)
      setAutoCreateMessage(err?.data?.message || "Error creating contact map.")
    }
  }


  // 4a - Map functions - getMapEntryId
  const getMapEntryId = (entry) => entry?._id || entry?.id
  
  // 4b - Map functions - getContactIdFromEntry
  const getContactIdFromEntry = (entry) =>
    entry?.contactId?._id ||
    entry?.contactId?.id ||
    entry?.contactId ||
    ""

  // 4c - Map functions - getReportsToContactIdFromEntry
  const getReportsToContactIdFromEntry = (entry) =>
    entry?.reportsToContactId?._id ||
    entry?.reportsToContactId?.id ||
    entry?.reportsToContactId ||
    entry?.reportsToAccountContactMapId?.contactId?._id ||
    entry?.reportsToAccountContactMapId?.contactId?.id ||
    entry?.reportsToAccountContactMapId?.contactId ||
    ""

  // 4d - Account contact map tree
 const accountContactMapTree = useMemo(() => {
    const childrenByParentContactId = {}
    const mappedContactIds = new Set()
    const virtualRootMap = {}

    // 1 - Capture all contacts already present in the map
    accountContactMaps?.forEach(entry => {
        const contactId = getContactIdFromEntry(entry)?.toString()

        if (contactId) {
        mappedContactIds.add(contactId)
        }
    })

    // 2 - Build parent child grouping
    accountContactMaps?.forEach(entry => {
        const reportsToContactId = getReportsToContactIdFromEntry(entry)?.toString()

        if (reportsToContactId && mappedContactIds.has(reportsToContactId)) {
        if (!childrenByParentContactId[reportsToContactId]) {
            childrenByParentContactId[reportsToContactId] = []
        }

        childrenByParentContactId[reportsToContactId].push(entry)
        } else if (reportsToContactId) {
        const reportsToContact = entry?.reportsToContactId

        virtualRootMap[reportsToContactId] = {
            _id: `virtual-${reportsToContactId}`,
            id: `virtual-${reportsToContactId}`,
            contactId: reportsToContact,
            isVirtualRoot: true,
        }

        if (!childrenByParentContactId[reportsToContactId]) {
            childrenByParentContactId[reportsToContactId] = []
        }

        childrenByParentContactId[reportsToContactId].push(entry)
        } else {
        if (!childrenByParentContactId.ROOT) {
            childrenByParentContactId.ROOT = []
        }

        childrenByParentContactId.ROOT.push(entry)
        }
    })

    // 3 - Build recursive tree
    const buildTree = (parentContactId = "ROOT", level = 0) => {
        return (childrenByParentContactId[parentContactId] || []).map(entry => {
        const contactId = getContactIdFromEntry(entry)?.toString()

        return {
            entry,
            level,
            children: buildTree(contactId, level + 1),
        }
        })
    }

    // 4 - Build virtual top roots as normal level 0 nodes
    const virtualRootTree = Object.values(virtualRootMap).map(entry => {
        const contactId = getContactIdFromEntry(entry)?.toString()

        return {
        entry,
        level: 0,
        children: buildTree(contactId, 1),
        }
    })

    return [...virtualRootTree, ...buildTree("ROOT", 0)]
  }, [accountContactMaps])

//   console.log('accountContactMapTree', accountContactMapTree)

// 4e - Scrollable
const ScrollablePeerRow = ({ children }) => {
  const scrollRef = useRef(null)

  const handleScroll = (direction) => {
    if (!scrollRef.current) return

    scrollRef.current.scrollBy({
      left: direction === "left" ? -320 : 320,
      behavior: "smooth",
    })
  }

  return (
    <div className="relative w-full max-w-4xl mx-auto bg-gray-50 rounded p-2">
      <button
        type="button"
        onClick={() => handleScroll("left")}
        className="absolute left-1 top-[4.5rem] bg-white z-10 p-1 rounded-full shadow border border-gray-200 hover:bg-orange-50"
        // className="absolute left-1 top-1/2 transform -translate-y-1/2 bg-white z-10 p-1 rounded-full shadow border border-gray-200 hover:bg-orange-50"
      >
        <ChevronLeftIcon className="h-5 w-5 text-orange-600" />
      </button>

      <div
        ref={scrollRef}
        className="flex overflow-x-auto gap-4 px-[2.5rem] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
      >
        {children}
      </div>

      <button
        type="button"
        onClick={() => handleScroll("right")}
        className="absolute right-1 top-[4.5rem] bg-white z-10 p-1 rounded-full shadow border border-gray-200 hover:bg-orange-50"
        // className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-white z-10 p-1 rounded-full shadow border border-gray-200 hover:bg-orange-50"
      >
        <ChevronRightIcon className="h-5 w-5 text-orange-600" />
      </button>
    </div>
  )
}

// 4f - AccountContactMapNode
const AccountContactMapCard = ({ node }) => {
  const item = node?.entry
  const contact = item?.contactId

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 m-1 p-2 w-56 min-w-[14rem] text-center">
      <div className="flex flex-col justify-center items-center">
        <UserCircleIcon className="h-6 w-6 text-orange-600 mb-1" />

        <p className="text-xs font-bold text-gray-800">
          {contact?.contactName || "Unnamed Contact"}
        </p>

        <p className="text-[9px] text-orange-600 mt-1 truncate w-full">
          {contact?.contactOrgTitle?.orgTitleName || "CxO"} ,{" "}
          {contact?.contactOrgRole?.orgRoleName || "CxO"}
        </p>

        <p className="text-[9px] text-gray-500 mt-1 truncate w-full">
          {contact?.contactEmail || "No email"}
        </p>

        <p className="text-[9px] text-gray-500 mt-1 truncate w-full">
          {contact?.contactPhone || "No phone"}
        </p>

        <div className="flex justify-center items-center flex-wrap mt-2 gap-1">
          <span className="text-[9px] bg-orange-100 text-orange-700 rounded-full px-2 py-1">
            {node?.level === 0 ? "CEO / Top" : `CEO - ${node?.level}`}
          </span>

          {item?.contactMapRole && (
            <span className="text-[9px] bg-gray-100 text-gray-700 rounded-full px-2 py-1">
              {item.contactMapRole}
            </span>
          )}

          {item?.isVirtualRoot && (
            <span className="text-[9px] bg-gray-100 text-gray-500 rounded-full px-2 py-1">
              Not mapped yet
            </span>
          )}
        </div>

        {!item?.isVirtualRoot && (
          <div className="flex justify-center items-center flex-wrap mt-2 gap-1">
            <span className="text-[9px] bg-gray-100 rounded px-2 py-1">
              {item?.influenceLevel || "NA"}
            </span>

            <span className="text-[9px] bg-gray-100 rounded px-2 py-1">
              {item?.relationshipStrength || "Unknown"}
            </span>

            <span className="text-[9px] bg-gray-100 rounded px-2 py-1">
              {item?.accessLevel || "NoAccess"}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// 4f - AccountContactMapNode
const AccountContactMapNode = ({ node }) => {
  return (
    <div className="flex flex-col items-center min-w-[14rem]">
      <AccountContactMapCard node={node} />

      {node?.children?.length > 0 && (
        <>
          <div className="h-5 border-l border-orange-300" />

          <ScrollablePeerRow>
            {node.children.map(childNode => (
              <div
                key={getMapEntryId(childNode?.entry)}
                className="flex flex-col items-center"
              >
                <AccountContactMapNode node={childNode} />
              </div>
            ))}
          </ScrollablePeerRow>
        </>
      )}
    </div>
  )
}

  // 5 - Error Handling / Loading 
  if (isAccountsLoading || isContactsLoading) {
    return <PulseLoader color={"#ea580c"} />
  }

  if (isAccountsError) {
    return (
      <div className="text-sm text-orange-700">
        Error loading Accounts: {accountsError?.data?.message}
      </div>
    )
  }

  if (isContactsError) {
    return (
      <div className="text-sm text-orange-700">
        Error loading Contacts: {contactsError?.data?.message}
      </div>
    )
  }

  return (
    <div className="bg-gray-200 m-1 p-1 rounded font-poppins mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center m-1 p-1">
        <div className="flex justify-start items-center">
          <p className="text-2xl m-1 p-1 font-bold">Account Contact Map</p>

          <img
            src={addIcon}
            alt=""
            className="h-10 w-10 cursor-pointer"
            onClick={onAdd}
          />
        </div>

        <button
          type="button"
          onClick={handleAutoCreateContactMap}
          disabled={!selectedAccountId || isCreatingMap}
          className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded px-3 py-2 disabled:bg-gray-400"
        >
          {isCreatingMap ? "Adding..." : "Auto Add Contacts"}
        </button>
      </div>

      {/* Account Selector */}
      <div className="bg-white m-1 p-2 rounded">
        <select
          value={selectedAccountId}
          onChange={(e) => {
            setSelectedAccountId(e.target.value)
            setAutoCreateMessage("")
          }}
          className="w-full rounded p-2 m-1 text-xs border border-gray-200"
        >
          <option value="">Select an Account</option>
          {accounts?.map(account => (
            <option key={account?._id || account?.id} value={account?._id || account?.id}>
              {account?.accountName}
            </option>
          ))}
        </select>

        {selectedAccount && (
          <div className="m-1 p-1 text-[10px] text-gray-600">
            <p>
              Client Org:{" "}
              <span className="font-bold">
                {selectedAccount?.contactOrganizationId?.contactOrganizationName ||
                  "Not linked"}
              </span>
            </p>
            <p>
              Contacts available:{" "}
              <span className="font-bold">{eligibleContacts?.length || 0}</span>
              {" "} | Already mapped:{" "}
              <span className="font-bold">{accountContactMaps?.length || 0}</span>
              {" "} | Missing:{" "}
              <span className="font-bold">{missingContacts?.length || 0}</span>
            </p>
          </div>
        )}
      </div>

      {autoCreateMessage && (
        <div className="bg-white m-1 p-2 rounded">
          <p className="text-xs text-orange-700">{autoCreateMessage}</p>
        </div>
      )}

      {selectedAccountId && isMapsLoading && <PulseLoader color={"#ea580c"} />}

      {selectedAccountId && isMapsError && (
        <div className="text-sm text-orange-700">
          Error loading Contact Map: {mapsError?.data?.message}
        </div>
      )}

      {/* 28 Apr 2026 */}
      {selectedAccountId && (
        <div className="bg-white rounded m-1 p-1 flex justify-start items-center">
            <button
            type="button"
            onClick={() => setActiveTab("details")}
            className={`text-xs font-bold rounded px-3 py-2 mx-1 ${
                activeTab === "details"
                ? "bg-orange-600 text-white"
                : "bg-gray-100 text-gray-700"
            }`}
            >
            Details
            </button>

            <button
            type="button"
            onClick={() => setActiveTab("visual")}
            className={`text-xs font-bold rounded px-3 py-2 mx-1 ${
                activeTab === "visual"
                ? "bg-orange-600 text-white"
                : "bg-gray-100 text-gray-700"
            }`}
            >
            Visual Map
            </button>
        </div>
      )}

      {!selectedAccountId && (
        <div className="bg-white rounded m-1 p-4">
          <p className="text-sm text-gray-600">
            Select an Account to view or build its Contact Map.
          </p>
        </div>
      )}

      {selectedAccountId && accountContactMaps?.length === 0 && !isMapsLoading && (
        <div className="bg-white rounded m-1 p-4">
          <p className="text-sm text-gray-600">
            No contacts mapped yet. Click Auto Add Contacts to add contacts from
            the linked Client Organization.
          </p>
        </div>
      )}

      {activeTab === "details" && accountContactMaps?.map((item, index) => {
        const contact = item?.contactId
        const reportsToSourceContact = item?.reportsToContactId // 28 Apr 2026
        const reportsToContact = item?.reportsToAccountContactMapId?.contactId

        return (
          <div key={item?._id || item?.id} className="m-1 p-1 bg-white rounded">
            <div className="m-1 p-1 bg-gray-100 rounded">
              <div className="flex justify-between items-center">
                <div className="flex justify-start items-center mx-2">
                  <div
                    onClick={() => handleToggleDetails(item?._id || item?.id)}
                    className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-white"
                  >
                    {showDetails[item?._id || item?.id] ? (
                      <MinusIcon className="h-6 w-6 text-orange-600" />
                    ) : (
                      <PlusIcon className="h-4 w-4 text-orange-600" />
                    )}
                  </div>

                  <div className="m-1 p-1">
                    <div className="flex justify-start items-center flex-wrap">
                      <p className="font-bold">
                        {index + 1} - {contact?.contactName || "Unnamed Contact"}
                      </p>

                      {item?.contactMapRole && (
                        <p className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1">
                          {item.contactMapRole}
                        </p>
                      )}

                      {item?.buyingRole && (
                        <p className="text-[10px] bg-gray-200 text-gray-700 rounded-full mx-1 px-2 py-1">
                          {item.buyingRole}
                        </p>
                      )}

                      {!item?.active && (
                        <p className="text-[10px] bg-red-100 text-red-700 rounded-full mx-2 px-2 py-1">
                          Inactive
                        </p>
                      )}
                    </div>

                    <p className="text-xs text-gray-600">
                      {contact?.contactEmail || "No email"}
                    </p>
                  </div>
                </div>

                <div className="flex justify-start items-center mx-2">
                  <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                    <PencilSquareIcon
                    //   onClick={() => onUpdate(item?._id || item?.id)}
                        onClick={() =>
                            onUpdate({
                                mapId: item?._id || item?.id,
                                accountId:
                                item?.accountId?._id ||
                                item?.accountId?.id ||
                                item?.accountId,
                            })
                        }
                      className="h-4 w-4 text-orange-600 cursor-pointer"
                    />
                  </div>

                  <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                    <TrashIcon
                      onClick={() => onDelete(item?._id || item?.id)}
                      className="h-4 w-4 text-orange-600 cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              {showDetails[item?._id || item?.id] && (
                <div className="m-1 p-1 bg-gray-200 rounded">
                  <div className="m-1 p-1 rounded bg-white">
                    <div className="flex justify-start items-center my-2">
                      <UserCircleIcon className="h-4 w-4 text-orange-600 mx-2" />
                      <p className="text-xs font-bold">
                        Contact:{" "}
                        <span className="font-normal text-gray-500">
                          {contact?.contactName || "Not defined"}
                        </span>
                      </p>
                    </div>

                    <div className="flex justify-start items-center my-2">
                      <BuildingOffice2Icon className="h-4 w-4 text-orange-600 mx-2" />
                      <p className="text-xs font-bold">
                        Email:{" "}
                        <span className="font-normal text-gray-500">
                          {contact?.contactEmail || "Not defined"}
                        </span>
                      </p>
                    </div>

                    {(reportsToSourceContact || reportsToContact) && (
                        <div className="flex justify-start items-center my-2">
                            <ArrowUpRightIcon className="h-4 w-4 text-orange-600 mx-2" />
                            <p className="text-xs font-bold">
                            Reports To:{" "}
                            <span className="font-normal text-gray-500">
                                {reportsToSourceContact?.contactName ||
                                reportsToContact?.contactName ||
                                "Not defined"}
                            </span>
                            </p>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-1 m-1 p-1">
                      <div className="bg-gray-100 rounded p-2">
                        <p className="text-[10px] font-bold text-gray-700">
                          Influence
                        </p>
                        <p className="text-[10px] text-gray-600">
                          {item?.influenceLevel || "Not defined"}
                        </p>
                      </div>

                      <div className="bg-gray-100 rounded p-2">
                        <p className="text-[10px] font-bold text-gray-700">
                          Relationship Strength
                        </p>
                        <p className="text-[10px] text-gray-600">
                          {item?.relationshipStrength || "Unknown"}
                        </p>
                      </div>

                      <div className="bg-gray-100 rounded p-2">
                        <p className="text-[10px] font-bold text-gray-700">
                          Sentiment
                        </p>
                        <p className="text-[10px] text-gray-600">
                          {item?.relationshipSentiment || "Unknown"}
                        </p>
                      </div>

                      <div className="bg-gray-100 rounded p-2">
                        <p className="text-[10px] font-bold text-gray-700">
                          Cadence
                        </p>
                        <p className="text-[10px] text-gray-600">
                          {item?.conversationCadence || "Not defined"}
                        </p>
                      </div>

                      <div className="bg-gray-100 rounded p-2">
                        <p className="text-[10px] font-bold text-gray-700">
                          Strategic Importance
                        </p>
                        <p className="text-[10px] text-gray-600">
                          {item?.strategicImportance || "Not defined"}
                        </p>
                      </div>

                      <div className="bg-gray-100 rounded p-2">
                        <p className="text-[10px] font-bold text-gray-700">
                          Access Level
                        </p>
                        <p className="text-[10px] text-gray-600">
                          {item?.accessLevel || "NoAccess"}
                        </p>
                      </div>
                    </div>

                    {item?.contactMapNotes?.length > 0 && (
                      <div className="m-1 p-1">
                        <p className="text-xs font-bold m-1 p-1">Notes</p>

                        {item.contactMapNotes.map((note, noteIndex) => (
                          <div
                            key={noteIndex}
                            className="rounded m-1 p-1 bg-gray-200"
                          >
                            <p className="text-[10px] mx-1 px-1">
                              {note?.noteText}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      })}

      {activeTab === "visual" && selectedAccountId && accountContactMaps?.length > 0 && (
        <div className="bg-gray-50 rounded m-1 p-3 overflow-x-auto">
            <div className="min-w-[700px] max-w-full flex flex-col items-center px-4">
            <div className="mb-3">
                <p className="text-sm font-bold text-gray-800">
                {selectedAccount?.accountName} Contact Organization Map
                </p>
                <p className="text-[10px] text-gray-500">
                Reporting structure based on mapped contact relationships.
                </p>
            </div>

            {accountContactMapTree?.length > 0 ? (
                accountContactMapTree.map(node => (
                <AccountContactMapNode
                    key={getMapEntryId(node?.entry)}
                    node={node}
                />
                ))
            ) : (
                <div className="bg-white rounded p-4">
                <p className="text-xs text-gray-600">
                    No reporting structure available yet. Please update contacts with Reports To details.
                </p>
                </div>
            )}
            </div>
        </div>
      )}
    </div>
  )
}

export default EtAccountContactMapList